/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, LinuxDeviceFile} from '@ion/core/src/types/entities';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {AG_CLASSES} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  parse
} from './ViewDeviceEditorUtils';

export interface LinuxDeviceFilesType {
  files: LinuxDeviceFile;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  linuxDevicefileGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      maxHeight                : '100vh',
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto',
        maxHeight : '300px'
      }
    }
  }
}));

/**
  * LinuxDeviceFiles component that displays the raw idreport device
  * data for the selected Device Details tab.
  */
export const LinuxDeviceFiles = ({
  files
}: LinuxDeviceFilesType) => {
  const classes                               = useStyles();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [rawDataLinux, setRawDataLinux]       = useState(null);

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi.showNoRowsOverlay();
    }
  }, [hasItems]);

  useEffect(() => {
    if (files) {
      setRawDataLinux(parse(files));
    }
  }, [files]);

  /*
   * Takes a list of linux device files raw idreport and constructs a list that
   * can be easily consumed by the IonGrid API
   */
  const constructLinuxDeviceFilesGridRows = useCallback(function(rawDataLinux: GenericObject[]): GridResult[] {
    const gridItems = rawDataLinux.map(rawData => ({
      ...rawData
    })) as GridResult[];
    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const getGridHeight = useCallback(function() {
    return classes.linuxDevicefileGrid;
  }, []);

  return (
    <div>
      <IonGrid omitHeader
        rowModelType={RowModelTypes.ClientSide}
        rowData={rawDataLinux}
        totalRows={rawDataLinux?.length}
        augmentItems={constructLinuxDeviceFilesGridRows}
        columnDefinitions={getColumnDefinitions()}
        checkboxesEnabled={false}
        showHeaderCheckbox={false}
        viewSubGroups={false}
        rowMenusEnabled={false}
        rowHeight={40}
        onGridReady={onGridReady}
        getGridHeight={getGridHeight}
        paginationEnabled
        showCustomPaginationBar
        pageOffset
        pageSizes={PAGE_SIZES} />
    </div>
  );
};

/**
  * Returns column definitions for desktop Services grid
  */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.index'),
    field      : 'index',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileType'),
    field      : 'fileType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileName'),
    field        : 'fileName',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'fileName'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileHash'),
    field        : 'fileHash',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'fileHash'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.version'),
    field        : 'Version',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'Version'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.status'),
    field        : 'Status',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'Status'
  }];
};

/**
  * Returns column definitions for mobile Services grid
  */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.index'),
    field      : 'index',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileType'),
    field      : 'fileType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileName'),
    field        : 'fileName',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'fileName'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.fileHash'),
    field        : 'fileHash',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'fileHash'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.version'),
    field        : 'Version',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'Version'
  },
  {
    headerName   : translate('admin-devices:view.idReport.linuxDeviceFilesGrid.status'),
    field        : 'Status',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    tooltipField : 'Status'
  }];
};
const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});
export default LinuxDeviceFiles;
